/**
 * Shared, theme-aware chart configuration: the categorical palette, axis/grid colors, point-marker
 * styling, tick font size, and date-label formatting used across every dashboard chart.
 *
 * The colors live in CSS variables (--chart-1 … --chart-10 in variables.scss) so each theme
 * (2025 / 2026) and future themes define their own graph colors in one place. Charts render to
 * canvas (Chart.js), so the values are resolved from the active theme via getComputedStyle
 * rather than referenced directly in CSS — the same pattern used for the KPI sparkline.
 *
 * Note: charts re-read the palette when they rebuild. A rebuild happens on dark-mode change and
 * on data reload, but not on a bare theme-version switch (2025 ⇄ 2026) — that takes effect on the
 * next reload, consistent with the rest of the theming.
 */

import type { Chart as ChartInstance, Plugin, ScriptableContext } from 'chart.js';

/**
 * 2025 palette; also the fallback when the CSS variables are unavailable (SSR/tests).
 * Ordered so the first few series sit far apart in hue.
 */
const FALLBACK_PALETTE: readonly string[] = [
  '#14b8a6', // teal (brand)
  '#516ce3', // blue
  '#1b9e4b', // green
  '#f97316', // orange
  '#8b5cf6', // violet
  '#84cc16', // lime
  '#ec4899', // pink
  '#d946ef', // fuchsia
  '#ef4444', // red
  '#a16207', // dark amber
];

/** Number of colors in the categorical palette (stable across themes). */
export const CHART_PALETTE_SIZE = FALLBACK_PALETTE.length;

/** Resolve the active theme's categorical chart palette from CSS variables. */
export function resolveChartPalette(): string[] {
  if (typeof document === 'undefined') {
    return [...FALLBACK_PALETTE];
  }
  const styles = getComputedStyle(document.body);
  const palette = FALLBACK_PALETTE.map(
    (fallback, index) => styles.getPropertyValue(`--chart-${index + 1}`).trim() || fallback,
  );
  // The first series is the brand colour, so a single-series chart is on-brand. --chart-1 points at
  // another variable, which getComputedStyle cannot resolve through the indirection, so read a literal:
  // --chart-brand (the graph colour, kept apart from the accent driving buttons) else the accent.
  const brand = styles.getPropertyValue('--chart-brand').trim() || styles.getPropertyValue('--accentColor').trim();
  if (brand) {
    palette[0] = brand;
  }
  return palette;
}

/**
 * A palette color at partial opacity, for area fills under a line. Handles the `#rgb` / `#rrggbb`
 * form every theme uses; anything else (a named color, an already-rgba value) is returned untouched
 * rather than mangled, which at worst means a solid fill instead of a translucent one.
 */
export function withAlpha(color: string, alpha: number): string {
  const hex = color?.trim() ?? '';
  const digits =
    hex.length === 4
      ? // #rgb → #rrggbb
        hex
          .slice(1)
          .split('')
          .map((digit) => digit + digit)
          .join('')
      : hex.length === 7
        ? hex.slice(1)
        : '';
  if (!hex.startsWith('#') || !/^[0-9a-f]{6}$/i.test(digits)) {
    return color;
  }
  const channel = (position: number) => parseInt(digits.slice(position, position + 2), 16);
  return `rgba(${channel(0)}, ${channel(2)}, ${channel(4)}, ${alpha})`;
}

/**
 * The card a chart sits on, for filling the centre of a point marker so it reads as a ring in the
 * series colour. Resolved per render because a canvas cannot reference a CSS variable.
 */
export function resolveChartSurfaceColor(isDark: boolean): string {
  const fallback = isDark ? '#3a4656' : '#fff';
  if (typeof document === 'undefined') {
    return fallback;
  }
  return getComputedStyle(document.body).getPropertyValue('--bgCard').trim() || fallback;
}

/**
 * Gridline and axis-label colors for the active theme. Same reasoning as the palette: the
 * values belong to the theme, but a canvas chart cannot reference a CSS variable, so they are
 * resolved here. `isDark` only picks the fallback for when the variables are unavailable.
 */
export function resolveChartAxisColors(isDark: boolean): { grid: string; text: string } {
  const fallbackGrid = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
  const fallbackText = isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.45)';
  if (typeof document === 'undefined') {
    return { grid: fallbackGrid, text: fallbackText };
  }
  const styles = getComputedStyle(document.body);
  return {
    grid: styles.getPropertyValue('--chartGridColor').trim() || fallbackGrid,
    text: styles.getPropertyValue('--chartAxisTextColor').trim() || fallbackText,
  };
}

/** Axis-tick font size shared by every dashboard chart (AnalyticsChartComponent included). */
export const CHART_TICK_FONT_SIZE = 11;

/** Past this many points on the x-axis, dot markers would crowd the line, so they are dropped. */
export const CHART_POINT_MARKER_LIMIT = 45;

/** Line curve shared by every dashboard chart, so a line reads the same everywhere. */
export const CHART_LINE_TENSION = 0.4;

/** Area-fill opacity for a line's background, so a fill reads the same everywhere. */
export const CHART_FILL_ALPHA = 0.2;

/** Round tooltip swatches, matching the round line-point markers, instead of Chart.js's default
 * square color box. Spread into a dataset's `plugins.tooltip`. */
export const CHART_TOOLTIP_POINT_STYLE = { usePointStyle: true } as const;

/**
 * Point-marker styling shared by every line chart: a hollow ring — filled with the card's own
 * background, bordered in the series colour — that disappears once there are too many points on
 * the axis to keep it legible.
 */
export function resolveChartPointStyle(
  pointCount: number,
  seriesColor: string,
  isDark: boolean,
): { pointRadius: number; pointBackgroundColor: string; pointBorderColor: string; pointBorderWidth: number } {
  return {
    pointRadius: pointCount > CHART_POINT_MARKER_LIMIT ? 0 : 3,
    pointBackgroundColor: resolveChartSurfaceColor(isDark),
    pointBorderColor: seriesColor,
    pointBorderWidth: 2,
  };
}

/** Index of the last day key (`YYYY-MM-DD`) when it is today, otherwise -1. */
export function todayPointIndex(dayKeys: string[]): number {
  const now = new Date();
  const pad = (value: number) => String(value).padStart(2, '0');
  const today = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  const last = dayKeys.length - 1;
  return dayKeys[last]?.slice(0, 10) === today ? last : -1;
}

/**
 * Keeps the point at `todayIndex` visible even when the other markers are hidden, so the pulse from
 * createTodayPulsePlugin has a marker to sit on. Spread after resolveChartPointStyle.
 */
export function resolveTodayPointStyle(
  todayIndex: number,
  pointStyle: { pointRadius: number },
): { pointRadius: (ctx: ScriptableContext<'line'>) => number } {
  return {
    pointRadius: (ctx) => (todayIndex >= 0 && ctx.dataIndex === todayIndex ? 3 : pointStyle.pointRadius),
  };
}

/**
 * A pulsing ring over today's point. The canvas can't animate one point on its own, so the ring
 * is a DOM element laid over the chart, repositioned after every draw. The canvas's parent must be
 * positioned. `datasetIndexes` limits it to some series, e.g. not a previous-period line.
 */
export function createTodayPulsePlugin(todayIndex: number, datasetIndexes?: number[]): Plugin<'line'> {
  const rings = new Map<number, HTMLElement>();
  const removeAll = () => {
    rings.forEach((ring) => ring.remove());
    rings.clear();
  };
  const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  return {
    id: 'todayPulse',
    afterDatasetsDraw: (chart: ChartInstance<'line'>) => {
      const container = chart.canvas.parentElement as HTMLElement;
      chart.data.datasets.forEach((dataset, datasetIndex) => {
        const point = chart.getDatasetMeta(datasetIndex).data[todayIndex];
        const visible =
          todayIndex >= 0 &&
          (!datasetIndexes || datasetIndexes.includes(datasetIndex)) &&
          chart.isDatasetVisible(datasetIndex) &&
          dataset.data[todayIndex] != null &&
          point;
        let ring = rings.get(datasetIndex);
        if (!visible) {
          ring?.remove();
          rings.delete(datasetIndex);
          return;
        }
        if (!ring) {
          ring = document.createElement('span');
          Object.assign(ring.style, {
            position: 'absolute',
            boxSizing: 'border-box',
            width: '8px',
            height: '8px',
            margin: '-4px 0 0 -4px',
            borderRadius: '50%',
            pointerEvents: 'none',
            // A ring, not a disc, so the hollow marker under it stays hollow.
            border: `2px solid ${String(dataset.borderColor)}`,
          });
          container.appendChild(ring);
          if (!reducedMotion) {
            // Grows by size rather than scale, so the ring's border stays 2px however wide it gets.
            ring.animate(
              [
                { width: '8px', height: '8px', margin: '-4px 0 0 -4px', opacity: 0.6 },
                { width: '40px', height: '40px', margin: '-20px 0 0 -20px', opacity: 0 },
              ],
              { duration: 1600, iterations: Infinity, easing: 'ease-out' },
            );
          }
          rings.set(datasetIndex, ring);
        }
        // Offset by the canvas's own position, in case the parent pads it.
        ring.style.left = `${chart.canvas.offsetLeft + point.x}px`;
        ring.style.top = `${chart.canvas.offsetTop + point.y}px`;
      });
    },
    afterDestroy: removeAll,
  };
}

/**
 * "22 Aug" — day before month. Every dashboard chart uses this order (AnalyticsChartComponent's
 * hand-built labels included); en-GB keeps it regardless of the app's own locale, where 'en'
 * (en-US) would print month-first instead.
 */
export function formatChartDayLabel(date: Date): string {
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

declare let Chart: any;

/**
 * Chart.js resolves tick/tooltip/legend font size at build time, so this must run before
 * `new Chart(...)` — set after, the very first chart on a page renders at the library default
 * (12px) until something else happens to flip the global default first.
 */
export function applyChartFontDefaults(): void {
  Chart.defaults.font.size = CHART_TICK_FONT_SIZE;
}

/**
 * Legend labels drawn as solid dots (fillStyle from strokeStyle, no border) instead of Chart.js's
 * default box-with-border swatch. Used as a `plugins.legend.labels.generateLabels` callback.
 */
export function dotLegendLabels(chart: any, borderRadius = 5): any[] {
  const items = Chart.defaults.plugins.legend.labels.generateLabels.call(chart, chart);
  items.forEach((item: any) => {
    item.fillStyle = item.strokeStyle;
    item.lineWidth = 0;
    item.borderRadius = borderRadius;
  });
  return items;
}

/** Read from the CSS variables per render, since a canvas cannot use var(). */
export function resolveMarkedDayColors(): { holiday: string; campaign: string } {
  const fallback = { holiday: '#ff6464', campaign: '#808080' };
  if (typeof document === 'undefined') {
    return fallback;
  }
  const styles = getComputedStyle(document.body);
  return {
    holiday: styles.getPropertyValue('--markedDayHoliday').trim() || fallback.holiday,
    campaign: styles.getPropertyValue('--markedDayCampaign').trim() || fallback.campaign,
  };
}
